import { inject, Injectable } from '@angular/core';

import { mealSortOrder } from '../../shared/constants/meal-type-sort-order';
import { MealSlot, MealType } from '../../shared/enums';
import { DiningLocation, MealItem } from '../../shared/interfaces';
import { DiningContent } from '../../shared/interfaces/dining';
import { SignageConfigurationModel } from '../config/models';
import { RuntimeSlideModel } from './models';
import { SlideDurationPolicyService } from './slide-duration-policy.service';
import { ContentKind, SlideLayout } from './enums';

const SERVED_SLOTS = [MealSlot.Breakfast, MealSlot.Lunch, MealSlot.Dinner];

// Four plain items still read comfortably on one backdrop; anything richer gets a slide of its own.
const MAX_COMBINED_MEALS = 4;

// One slide per location and meal slot, combining short courses so nobody waits through four near-empty screens for one lunch.
@Injectable({ providedIn: 'root' })
export class DiningSlideBuilder {
  private readonly duration = inject(SlideDurationPolicyService);

  public build(meals: MealItem[], locations: DiningLocation[], config: SignageConfigurationModel): RuntimeSlideModel[] {
    const slides: RuntimeSlideModel[] = [];

    for (const location of locations) {
      for (const slot of SERVED_SLOTS) {
        const available = meals.filter(meal => meal.typeId !== MealType.Unknown
          && meal.locationIds.includes(location.id) && meal.mealSlotIds.includes(slot));

        this.groupCourses(available).forEach((diningContent, index) =>
          slides.push(this.slideFor(location, locations.length, slot, index, diningContent, config)));
      }
    }

    return slides;
  }

  private slideFor(location: DiningLocation, locationCount: number, slot: MealSlot, index: number,
                   diningContent: DiningContent[], config: SignageConfigurationModel): RuntimeSlideModel {
    return {
      id: `dining:${location.id}:${slot}:${index}`, kind: ContentKind.Dining, layout: SlideLayout.Backdrop, mealSlot: slot,
      duration: this.duration.timingFor('', config), meta: { sourceId: 0 },
      content: {
        id: 0, title: `DINING.SLOTS.${MealSlot[slot]}`, titleLabel: locationCount > 1 ? 'DINING.DiningAt' : '',
        diningLocation: location, providerChannelIds: [0], diningContent
      }
    };
  }

  private groupCourses(meals: MealItem[]): DiningContent[][] {
    const groups: DiningContent[][] = [];
    let pending: DiningContent[] = [];
    let count = 0;

    for (const type of this.courseOrder()) {
      const courseMeals = meals.filter(meal => meal.typeId === type);

      if (!courseMeals.length) {
        continue;
      }

      count += courseMeals.length;
      // A course with descriptions or nutritional info needs the room, so it breaks the run either way.
      const canCombine = count <= MAX_COMBINED_MEALS
        && courseMeals.every(meal => !meal.description && !meal.nutritionalInfo);

      // Flushed before and after: what was accumulating gets its own slide, then the rich course gets one.
      if (!canCombine && pending.length) {
        groups.push(pending);
        pending = [];
        count = 0;
      }

      pending.push(this.courseFor(type, courseMeals));

      if (!canCombine) {
        groups.push(pending);
        pending = [];
        count = 0;
      }
    }

    if (pending.length) {
      groups.push(pending);
    }

    return groups;
  }

  private courseFor(type: MealType, meals: MealItem[]): DiningContent {
    return {
      course: `DINING.COURSES.${MealType[type]}`,
      meals: meals.map(meal => ({
        label: meal.label, description: meal.description || '', info: meal.nutritionalInfo || ''
      }))
    };
  }

  private courseOrder(): MealType[] {
    return Object.keys(mealSortOrder).map(Number).filter(type => type !== MealType.Unknown)
      .sort((a, b) => mealSortOrder[a as MealType] - mealSortOrder[b as MealType]);
  }
}
